import {
    CASH_WALLET_INVOICE_CONTENT_TYPE,
    CASH_WALLET_INVOICE_MAGIC_BYTES,
    CASH_WALLET_INVOICE_MAX_SIZE_BYTES,
    CASH_WALLET_VAT_RATE,
    CASH_WALLET_WITHDRAWAL_MAX_AMOUNT,
    WITHDRAWAL_BUTTON_STATE,
    WITHDRAWAL_CANCEL_ERROR,
    WITHDRAWAL_REQUEST_STATUS,
    WITHDRAWAL_REQUESTS_PAGE_SIZE,
    WITHDRAWAL_TAX_CLASSIFICATION,
} from '@arbitrip/constants';
import {
    deriveNetVat as sharedDeriveNetVat,
    maxWithdrawableNet as sharedMaxWithdrawableNet,
    AMOUNT_RE as SHARED_AMOUNT_RE,
} from '@arbitrip/utils/money';

// IN_REVIEW is excluded on purpose: the API masks In Review down to Submitted for agents (AR-3066), so
// an agent payload can never carry it and nothing here may branch on it.
const { IN_REVIEW: IN_REVIEW_MASKED_FOR_AGENTS, ...AGENT_REQUEST_STATUS } = WITHDRAWAL_REQUEST_STATUS;

// Mirrored by hand, not imported — the codes originate in two places, neither importable here:
//   - packages/cash-wallet/submit-handler.js `SUBMIT_ERROR` (every code but INVALID_UPLOAD) — that
//     package lazily requires @arbitrip/mongodb, so the web bundle must not pull it in.
//   - services/api/routes/private/cash-wallet.js — INVALID_UPLOAD, rejected by the multipart layer
//     before the handler runs.
// Every code needs a matching `SubmitError.<CODE>` translation.
const SUBMIT_ERROR = {
    FROZEN: 'FROZEN',
    ACTIVE_REQUEST: 'ACTIVE_REQUEST',
    MONTHLY_CAP_REACHED: 'MONTHLY_CAP_REACHED',
    TAX_CLASSIFICATION_REQUIRED: 'TAX_CLASSIFICATION_REQUIRED',
    INVALID_AMOUNT: 'INVALID_AMOUNT',
    BELOW_THRESHOLD: 'BELOW_THRESHOLD',
    ABOVE_BALANCE: 'ABOVE_BALANCE',
    ABOVE_MAX_AMOUNT: 'ABOVE_MAX_AMOUNT',
    INVOICE_REQUIRED: 'INVOICE_REQUIRED',
    INVOICE_INVALID_TYPE: 'INVOICE_INVALID_TYPE',
    INVOICE_TOO_LARGE: 'INVOICE_TOO_LARGE',
    INVALID_UPLOAD: 'INVALID_UPLOAD',
    UPLOAD_FAILED: 'UPLOAD_FAILED',
    CREATE_FAILED: 'CREATE_FAILED',
};

// A value that is a valid amount SO FAR while typing — deliberately more permissive than AMOUNT_RE
// (which demands a complete number) so intermediate states survive a keystroke: '', '6', '666.',
// '666.9'. It still refuses a third decimal, so `666.955` can never be typed or pasted into the amount
// input at all. Not a substitute for AMOUNT_RE: submission still validates the completed value.
const PARTIAL_AMOUNT_RE = /^\d*(\.\d{0,2})?$/;

// Each partition owns one drawer field's inline error.
const TAX_INLINE_CODES = [SUBMIT_ERROR.TAX_CLASSIFICATION_REQUIRED];
const AMOUNT_INLINE_CODES = [
    SUBMIT_ERROR.INVALID_AMOUNT,
    SUBMIT_ERROR.BELOW_THRESHOLD,
    SUBMIT_ERROR.ABOVE_BALANCE,
    SUBMIT_ERROR.ABOVE_MAX_AMOUNT,
];
const INVOICE_INLINE_CODES = [
    SUBMIT_ERROR.INVOICE_REQUIRED,
    SUBMIT_ERROR.INVOICE_INVALID_TYPE,
    SUBMIT_ERROR.INVOICE_TOO_LARGE,
    SUBMIT_ERROR.INVALID_UPLOAD,
];
// The page decides toast-vs-inline and MUST agree with the drawer on every code, or an error is either
// double-reported or silently swallowed.
const SUBMIT_INLINE_CODES = [...TAX_INLINE_CODES, ...AMOUNT_INLINE_CODES, ...INVOICE_INLINE_CODES];

const CashWalletConstants = {
    RETRIEVE_DASHBOARD: 'RETRIEVE_DASHBOARD',
    DASHBOARD_SUCCEEDED: 'DASHBOARD_SUCCEEDED',
    DASHBOARD_FAILED: 'DASHBOARD_FAILED',

    RETRIEVE_REQUESTS: 'RETRIEVE_REQUESTS',
    REQUESTS_SUCCEEDED: 'REQUESTS_SUCCEEDED',
    REQUESTS_FAILED: 'REQUESTS_FAILED',

    RETRIEVE_REQUEST_DETAIL: 'RETRIEVE_REQUEST_DETAIL',
    REQUEST_DETAIL_SUCCEEDED: 'REQUEST_DETAIL_SUCCEEDED',
    REQUEST_DETAIL_FAILED: 'REQUEST_DETAIL_FAILED',

    CANCEL_REQUEST: 'CANCEL_REQUEST',
    CANCEL_SUCCEEDED: 'CANCEL_SUCCEEDED',
    CANCEL_FAILED: 'CANCEL_FAILED',

    SUBMIT_REQUEST: 'SUBMIT_REQUEST',
    SUBMIT_SUCCEEDED: 'SUBMIT_SUCCEEDED',
    SUBMIT_FAILED: 'SUBMIT_FAILED',
    RESET_SUBMIT: 'RESET_SUBMIT',

    STATUS: {
        INITIAL: 'initial',
        BUSY: 'busy',
        SUCCESS: 'success',
        ERROR: 'error',
    },

    BUTTON_STATE: WITHDRAWAL_BUTTON_STATE,
    REQUEST_STATUS: AGENT_REQUEST_STATUS,
    TAX_CLASSIFICATION: WITHDRAWAL_TAX_CLASSIFICATION,
    CANCEL_ERROR: WITHDRAWAL_CANCEL_ERROR,
    SUBMIT_ERROR,

    TAX_INLINE_CODES,
    AMOUNT_INLINE_CODES,
    INVOICE_INLINE_CODES,
    SUBMIT_INLINE_CODES,

    INVOICE_MAX_SIZE_BYTES: CASH_WALLET_INVOICE_MAX_SIZE_BYTES,
    INVOICE_ACCEPT: CASH_WALLET_INVOICE_CONTENT_TYPE,
    INVOICE_EXTENSION: '.pdf',
    PDF_MAGIC: CASH_WALLET_INVOICE_MAGIC_BYTES,
    // The server's own pattern (packages/utils/money.js) — never a local copy.
    AMOUNT_RE: SHARED_AMOUNT_RE,

    DATE_FORMAT: 'D MMM YYYY',
    PAGE_SIZE: WITHDRAWAL_REQUESTS_PAGE_SIZE,
    FIRST_PAGE: 1,
    DEFAULT_CURRENCY: 'USD',
};

// The agent enters a NET figure. `vat` is derived from it at the statutory rate for BOTH tax
// classifications — the wallet's VAT liability exists regardless of the agent's own classification.
// The returned `amount` is the agent's PAYOUT: gross (net + vat) for Authorised, who remits the VAT
// themselves; net-only for Exempt, whose VAT is remitted by the company directly and never reaches them.
// Callers decide whether `taxClassification` is a real selection at all (e.g. the withdrawal drawer's
// "nothing picked yet" preview state) — this always computes real numbers, matching the backend exactly.
export function deriveNetVat(amount, taxClassification) {
    const isAuthorised = taxClassification === CashWalletConstants.TAX_CLASSIFICATION.AUTHORISED;
    return sharedDeriveNetVat(amount, CASH_WALLET_VAT_RATE, isAuthorised);
}

// The stored wallet balance is GROSS (VAT-inclusive); the agent is always shown — and always spends
// against — its NET value. Unlike deriveNetVat this never depends on the tax classification.
//
// This is the SPENDABLE net ceiling (the inverse of the debit the ledger posts on Paid), NOT the plain
// `balance / (1 + VAT_RATE)` display conversion — the two differ by a cent for ~8% of balances, and that
// cent is what strands a "withdraw everything" request In Review. The dashboard serves the same figure
// as `net_balance`; this is only the fallback for a payload predating it.
export function maxWithdrawableNet(balance) {
    return sharedMaxWithdrawableNet(balance, CASH_WALLET_VAT_RATE);
}

// The maximum the agent's PAYOUT TOTAL may reach — gross for Authorised, net-only for Exempt, i.e. the
// drawer's "Total" / "You'll receive" figure, never the entered NET. The dashboard serves the same figure
// as `max_amount`; this is only the fallback for a payload predating it, exactly as `maxWithdrawableNet`
// is the fallback for `net_balance`. Unknown currency → 0, which blocks every amount: the same
// fail-closed default the API uses, so client and server refuse the same unrecognised currency.
export function getMaxAmount(currency) {
    return CASH_WALLET_WITHDRAWAL_MAX_AMOUNT[currency] || 0;
}

// Gate for the amount input's onChange: the keystroke/paste is only let through when the resulting
// value is still on its way to a valid amount. A rejected keystroke is a no-op, not an error state.
export function isPartialAmountInput(value) {
    return PARTIAL_AMOUNT_RE.test(String(value));
}

export default CashWalletConstants;
